/**
 * Panel de conexiones/empalmes de un nodo
 * @module components/panels/ConnectionsPanel
 */

import { useState, useEffect } from 'react'
import { connectionServiceApi, NodeConnectionsResult, FiberConnection } from '@/services/connectionServiceApi'
import { Cable, Link2, Plus, Trash2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { FiberConnectionForm } from '@/components/forms/FiberConnectionForm'
import { useFiberStore } from '@/stores'
import { liberarFibraPorExtremo } from '@/services/connectionService'
import { cableEndpointAtNode } from '@/utils/cableEndpoints'

interface ConnectionsPanelProps {
  nodeId: string
  nodeType: string
  nodeName?: string
}

export function ConnectionsPanel({ nodeId, nodeType, nodeName }: ConnectionsPanelProps) {
  const cables = useFiberStore((state) => state.cables)
  const updateCable = useFiberStore((state) => state.updateCable)
  const [data, setData] = useState<NodeConnectionsResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedBandejas, setExpandedBandejas] = useState<Set<number>>(new Set([0]))
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadConnections()
  }, [nodeId])

  const loadConnections = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await connectionServiceApi.getByNode(nodeId)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando conexiones')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteConnection = async (conn: FiberConnection) => {
    if (!confirm('¿Eliminar esta conexión?')) return

    try {
      await connectionServiceApi.delete(conn.id)

      // Devolver a "libre" solo el extremo de cada fibra que se usó en ESTE
      // empalme (conn.nodeId) -- el otro extremo de cada cable puede seguir
      // usándose para otra cosa y no debe tocarse.
      const cableEntrada = cables.find((c) => c.id === conn.cableEntradaId)
      const cableSalida = cables.find((c) => c.id === conn.cableSalidaId)
      const extremoEntrada = cableEntrada ? cableEndpointAtNode(cableEntrada, conn.nodeId) : null
      const extremoSalida = cableSalida ? cableEndpointAtNode(cableSalida, conn.nodeId) : null

      if (cableEntrada && cableSalida && extremoEntrada && extremoSalida && cableEntrada.id === cableSalida.id) {
        const liberado = liberarFibraPorExtremo(
          liberarFibraPorExtremo(cableEntrada as any, extremoEntrada, conn.bufferEntrada ?? null, conn.fibraEntrada),
          extremoSalida,
          conn.bufferSalida ?? null,
          conn.fibraSalida
        )
        await updateCable(liberado as any)
      } else {
        if (cableEntrada && extremoEntrada) {
          await updateCable(
            liberarFibraPorExtremo(cableEntrada as any, extremoEntrada, conn.bufferEntrada ?? null, conn.fibraEntrada) as any
          )
        }
        if (cableSalida && extremoSalida) {
          await updateCable(
            liberarFibraPorExtremo(cableSalida as any, extremoSalida, conn.bufferSalida ?? null, conn.fibraSalida) as any
          )
        }
      }

      await loadConnections()
    } catch (err) {
      alert('Error al eliminar la conexión')
    }
  }

  const toggleBandeja = (bandeja: number) => {
    const newExpanded = new Set(expandedBandejas)
    if (newExpanded.has(bandeja)) {
      newExpanded.delete(bandeja)
    } else {
      newExpanded.add(bandeja)
    }
    setExpandedBandejas(newExpanded)
  }

  const getConnectionTypeLabel = (tipo: string) => {
    const labels: Record<string, string> = {
      FUSION: 'Fusión',
      MECANICO: 'Mecánico',
      CONECTOR: 'Conector',
      SPLITTER: 'Splitter'
    }
    return labels[tipo] || tipo
  }

  const getEstadoColor = (estado: string) => {
    const colors: Record<string, string> = {
      ACTIVO: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      INACTIVO: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      PENDIENTE: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      EN_REPARACION: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    }
    return colors[estado] || ''
  }

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500">
        <div className="animate-spin inline-block w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        <p className="mt-2 text-sm">Cargando conexiones...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        <AlertCircle className="w-8 h-8 mx-auto mb-2" />
        <p className="text-sm">{error}</p>
        <button
          onClick={loadConnections}
          className="mt-2 text-xs text-primary-600 hover:underline"
        >
          Reintentar
        </button>
      </div>
    )
  }

  const bandejas = Object.keys(data?.byBandeja || {}).map(Number).sort((a, b) => a - b)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold">Empalmes</h3>
          <span className="text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 px-2 py-0.5 rounded-full">
            {data?.totalConnections || 0}
          </span>
        </div>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="p-1.5 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center gap-1"
            title="Nuevo empalme"
          >
            <Plus className="w-3 h-3" />
            Nuevo
          </button>
        )}
      </div>

      {/* Formulario de nuevo empalme */}
      {creating && (
        <FiberConnectionForm
          puntoEmpalmeId={nodeId}
          puntoEmpalmeNombre={nodeName || nodeId}
          onCreated={() => {
            setCreating(false)
            loadConnections()
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {/* No connections */}
      {!creating && (!data?.connections || data.connections.length === 0) && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <Cable className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Sin empalmes registrados</p>
          <p className="text-xs mt-1">
            {nodeType === 'CIERRE' 
              ? 'Agrega empalmes para documentar las conexiones de fibra'
              : nodeType === 'NAP'
              ? 'Registra las conexiones de los puertos'
              : 'Este tipo de nodo normalmente no tiene empalmes'}
          </p>
        </div>
      )}

      {/* Connections by bandeja */}
      {bandejas.length > 0 && (
        <div className="space-y-2">
          {bandejas.map((bandeja) => {
            const connections = data?.byBandeja[bandeja] || []
            const isExpanded = expandedBandejas.has(bandeja)

            return (
              <div key={bandeja} className="border rounded-lg dark:border-dark-600">
                {/* Bandeja header */}
                <button
                  onClick={() => toggleBandeja(bandeja)}
                  className="w-full px-3 py-2 flex items-center justify-between text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 rounded-t-lg"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    <span>
                      {bandeja === 0 ? 'Sin bandeja asignada' : `Bandeja ${bandeja}`}
                    </span>
                  </div>
                  <span className="text-xs bg-gray-200 dark:bg-dark-600 px-2 py-0.5 rounded">
                    {connections.length} conexiones
                  </span>
                </button>

                {/* Connections list */}
                {isExpanded && (
                  <div className="border-t dark:border-dark-600">
                    {connections.map((conn) => (
                      <div
                        key={conn.id}
                        className="px-3 py-2 border-b last:border-b-0 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 text-xs space-y-1">
                            {/* Connection type & status */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {conn.refCode && (
                                <span className="font-mono text-[11px] text-primary-600 dark:text-primary-400">
                                  {conn.refCode}
                                </span>
                              )}
                              <span className="font-medium">
                                {getConnectionTypeLabel(conn.tipo)}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${getEstadoColor(conn.estado)}`}>
                                {conn.estado}
                              </span>
                              {conn.posicion && (
                                <span className="text-gray-500">Pos. {conn.posicion}</span>
                              )}
                            </div>

                            {/* Input fiber */}
                            <div className="text-gray-600 dark:text-gray-400">
                              <span className="text-green-600 dark:text-green-400">→</span>
                              {' '}Entrada: Fibra {conn.fibraEntrada}
                              {conn.bufferEntrada && ` (Buffer ${conn.bufferEntrada})`}
                            </div>

                            {/* Output fiber */}
                            <div className="text-gray-600 dark:text-gray-400">
                              <span className="text-blue-600 dark:text-blue-400">←</span>
                              {' '}Salida: Fibra {conn.fibraSalida}
                              {conn.bufferSalida && ` (Buffer ${conn.bufferSalida})`}
                            </div>

                            {/* Attenuation */}
                            {conn.atenuacion !== null && conn.atenuacion !== undefined && (
                              <div className="text-gray-500">
                                Atenuación: {conn.atenuacion.toFixed(2)} dB
                              </div>
                            )}

                            {/* Observations */}
                            {conn.observaciones && (
                              <div className="text-gray-500 italic">
                                {conn.observaciones}
                              </div>
                            )}
                          </div>

                          {/* Delete button */}
                          <button
                            onClick={() => handleDeleteConnection(conn)}
                            className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
